/**
 * Radix's modal layers (dialogs, dropdown menus, selects) lock the page with
 * `body { pointer-events: none }` while open and restore whatever value they
 * found when they close. When a dialog opens from a menu item in the same
 * tick, the dialog records the MENU's lock as the "original" value and puts it
 * back on close: nothing on screen is open, yet nothing can be clicked until
 * a reload.
 */

/**
 * Anything that legitimately holds the lock while it is on screen. Modal
 * popovers and drawers carry role="dialog". Radix's popper wrapper is not here:
 * tooltips and hover cards use it too and never lock the page, so one on
 * screen must not protect a stranded lock.
 */
const OPEN_MODAL_LAYER = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[role="menu"][data-state="open"]',
  '[role="listbox"][data-state="open"]',
].join(',');

/**
 * Open a dialog from a menu item on the next task, out of the menu's own select
 * handler. This does not wait for the menu's exit animation; if a lock is still
 * stranded when the dialog closes, PointerLockGuard clears it.
 */
export function openAfterMenuCloses(open: () => void): void {
  setTimeout(open, 0);
}

/**
 * Clears a lock nothing is holding. Returns true when it had to. A lock with
 * an open dialog, menu or select is left alone.
 */
export function releaseStrandedPointerLock(doc: Document = document): boolean {
  const { body } = doc;
  if (!body || body.style.pointerEvents !== 'none') return false;
  if (doc.querySelector(OPEN_MODAL_LAYER)) return false;
  body.style.removeProperty('pointer-events');
  return true;
}
